/*
 * @providesModule SKZMatch
 *
 *
 * Created by Skillz on 2/23/16.
 *   Copyright © 2016 Skillz. All rights reserved.
 *
 */

import _ from 'lodash';
import I18n, {_m} from 'INTL';
import Bracket from 'SKZBracket';
import LoadingIndicator from 'LoadingIndicator';
import MatchesStore from 'MatchesStore';
import MatchPlayer from 'SKZMatchPlayer';
import SkillzStyle from 'SkillzStyle';
import TournamentStore from 'TournamentStore';
import type {BracketState} from 'SKZBracket';

import {NativeModules, Platform} from 'react-native';
import ClientPropertyConstants from 'ClientPropertyConstants';

const MERSENNE = 'MERSENNE';
const RANDOM_NUMBER_SEED = 'random_number_seed';
const RANDOM_NUMBER_SEEDS = 'random_number_seeds';

const SKZCoreDataWrapper = NativeModules.SKZCoreDataWrapper;
type MatchRandomNumberStrategy = 'MERSENNE' | 'LEGACY';
type MatchRecordCategory = 'YOUR_TURN' | 'COMPLETED' | 'CANCELED' | 'PENDING';
type MatchRecordType = 'UNLIMITED_ENTRY' | 'LIMITED_ENTRY' | 'REMATCH' | 'TURN_BASED';
type CustomServerConnection = {
  port: string;
  match_id: string;
  ip: string;
  externalSyncToken: string;
};
export class MatchRecord {
  id: string;
  name: string;
  tournament_id: number;
  started_at: string;
  expires_at: string;
  category: MatchRecordCategory;
  type: MatchRecordType;
  can_challenge_rematch: boolean;
  is_rematch: boolean;
  staff_refund: boolean;
  num_players: number;
  next_tie_breaker?: Object;
  players?: MatchPlayer[];
  // turns:          Array<any>;
  last_turn_ended_at?: string;
  finished_at?: string;
  random_number_seed?: number;
  random_number_seeds?: Array<number>;
  hasBeenUpdated?: boolean;
  random_number_strategy: MatchRandomNumberStrategy;
  entries_remaining?: number;

  share: {
    url: string;
    subject: string;
    message: string;
    replay_url: string;
  };

  record_replay?: boolean;
  rules: Array<string>;

  score_messages: {
    your: string;
    best: string;
    last_entry: string;
    in_progress: string;
  };
  bracket?: {
    id: string;
    position: number;
    expires_at?: string;
    state: BracketState;
  };
  cash_fee: number;
  z_fee: number;
  dynamic_prize_pool?: number;
  dynamic_points_prize_pool?: number;
  previous_ties: Array<string>;
  tie_breaker: boolean;
  connect_token?: string;
  custom_server_connection?: CustomServerConnection;
  match_code: string;
  hosted_tournament_logo?: string;
  custom_color?: string;
  brand_logo?: string;
  formatted_participation_message?: string;
  results_share_message?: string;
  skillz_powered_image_url?: string;
  welcome_back_override?: boolean;
  welcome_back_override_theme_type: 'FULL' | 'PARTIAL' | null;
  new_paying_user: boolean | null; // Is true only in the following flow: New User has made a deposit in ANY Skillz Game, then triggers Match Join for 1st cash match after depositing. Is false for all other match joins. Returns null on GET Match Join for all matches.
  action_type?: string; // Type of action to show on result screen (e.g., 'LTO', 'FTUE')
  action_meta?: {
    offer_id?: string | null;
    deposit_amount?: number | null;
    bonus_amount?: number | null;
    ftue_meta_data?: {
      percentile?: number | null;
      percentile_rank?: number | null;
      recommended_lto_id?: string | null;
      recommended_template_id?: number | null;
      message?: string | null;
    };
  };
}

export default class Match extends MatchRecord {
  constructor(keyValueSource?: MatchRecord) {
    super();
    Match._formatPlayers(keyValueSource);
    Match._formatSyncToken(keyValueSource);
    return _.create(Match.prototype, Match.getMatchData(keyValueSource));
  }

  static make(matches: Array<MatchRecord>): Array<Match> {
    if (Array.isArray(matches)) {
      return _.map(matches, (matchRecord: MatchRecord): Match => new Match(matchRecord));
    }
    return [];
  }

  static _formatPlayers(match?: MatchRecord) {
    if (!match?.players) return;

    const trainer = Match.getTrainerOpponent(match);
    if (trainer && ClientPropertyConstants.ftueConversionsEnabled) {
      const user = match.players.find(({user_id}) => user_id !== trainer.user_id);

      // Set the trainer's score to less than the user's and at most 100 (to make
      // it look like the trainer is fixed) but at least 0
      // TODO: Don't override the bot score on the SDK side; see if we can have
      //       backend do some kind of default when no bot_score is reported
      trainer.score = Math.max(Math.min(user.score - 1, 100), 0);
      trainer.state = 'FINISHED';
    }

    match.players = match.players.map((player: Object) => new MatchPlayer(player));
  }

  static _formatSyncToken(keyValueSource?: MatchRecord) {
    if (keyValueSource?.connect_token) {
      // Sync token will come down from server as a string of just the token in a skillz-server sync game
      // In a custom-server sync game the token will come down as a stringified JSON of the connection data
      // To tell the difference, we must attempt to parse the token and if it throws that means it was a connect-token
      // for skillz-server sync.
      try {
        const parsedToken = JSON.parse(keyValueSource.connect_token);
        // if we got here, it was a custom server connection
        console.debug('SKZMatch: parsed connect token as a custom server sync match');
        keyValueSource.custom_server_connection = parsedToken;
        keyValueSource.connect_token = undefined;
      } catch (error) {
        console.debug(
          'SKZMatch: connect token could not be parsed, assume a skillz server sync match'
        );
        // do nothing else here, the match is alright as-is
      }
    }
  }

  get bracket_position(): number {
    return _.get(this.bracket, 'position', 0);
  }

  get bracket_id(): number {
    return _.get(this.bracket, 'id', 0);
  }

  /**
   * Returns whether there is a bot opponent in this match
   */
  get isBotMatch(): boolean {
    return !!this.players?.some(({is_bot}) => is_bot);
  }

  shouldShowPendingAPNSTutorial(userId: number): boolean {
    return Boolean(!this.isCompleted() || (this.bracket && this.didWinMatch(userId)));
  }

  didGetBye(): boolean {
    if (this.isLiveBracket()) {
      let byePlaceholderPlayer;
      _.forEach(this.players, (player: MatchPlayer) => {
        if (player.isPlaceholderByePlayer()) {
          byePlaceholderPlayer = player;
        }
      });

      if (byePlaceholderPlayer) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  didGetByeV2(): boolean {
    if (this.isLiveBracket()) {
      // BE now only sends 1 player object (the actual player) in bye scenarios
      // A bye is detected when there's only 1 player in the match
      return !!(this.players && this.players.length === 1);
    } else {
      return false;
    }
  }

  shouldShowCompletedPracticeTutorial(userId: number): boolean {
    return (
      !this.bracket &&
      this.isCompleted() &&
      (this.isPointsMatch() || this.has_bot) &&
      !this.isHeadToHeadTieBreakerAvailable(userId)
    );
  }

  shouldShowPendingLossWinTutorial(userId: number): boolean {
    return !this.bracket && (!this.isCompleted() || !this.didWinMatch(userId));
  }

  isPointsMatch(): boolean {
    return this.z_fee > 0;
  }

  isH2HMatch(): boolean {
    return !this.bracket && this.type != 'UNLIMITED_ENTRY';
  }

  isDeclinedRematch(userId: string): boolean {
    const opponent = this.getTopOpponent(Number(userId), true);
    return Boolean(this.is_rematch && this.finished_at && (!opponent || !opponent.user_id));
  }

  didWinPrize(userId: number): boolean {
    if (!this.isCompleted()) {
      return false;
    }
    const player = this.getUserPlayer(userId);
    if (!player) {
      return false;
    }
    return (
      player.cash_prize > 0 ||
      player.bonus_cash_prize > 0 ||
      player.z_prize > 0 ||
      player.custom_prize != null
    );
  }

  // has player definitively won match
  didWinMatch(userId: number): boolean {
    const player = this.getUserPlayer(userId);
    if (!player) {
      return false;
    }
    return this.isCompleted() && player.position == 1 && !this.isTie(userId);
  }

  // has player definitively lost match
  didLoseMatch(playerId: number): boolean {
    const player = this.getUserPlayer(playerId);
    if (!player) {
      return false;
    }
    return this.isCompleted() && player.position > 1;
  }

  isCompleted(): boolean {
    return this.finished_at !== null;
  }

  hasRefund(): boolean {
    return this.staff_refund;
  }

  wasRefunded(playerId: number): boolean {
    const player = this.getUserPlayer(playerId);

    if (!player) {
      return false;
    }
    return this.staff_refund && !player.is_staff && this.didLoseMatch(playerId);
  }

  isTie(userId: number): boolean {
    if (!this.finished_at) {
      return false;
    }
    const currentUsersPosition = this.usersPosition(userId);
    let isTie = false;
    this.players.some((player: MatchPlayer): boolean => {
      if (player.position == currentUsersPosition && player.user_id != userId) {
        isTie = true;
        return true;
      }
      return false;
    });

    // TODO: Test without the this.finished_at and remove if possible
    return Boolean(
      (isTie || this.isPlayableTieBreaker(userId)) &&
        ((this.bracket != null && this.finished_at != null) || this.bracket == null)
    );
  }

  hasTiesAvailable(): boolean {
    return (
      this.tie_number != 0 || (Array.isArray(this.previous_ties) && this.previous_ties.length != 0)
    );
  }

  // This is just checking isTie and isAborted, should not be used to check for sync tiebreakers.
  isTieBreakerAvailable(userId: number): boolean {
    return this.isTie(userId) && !this.isAborted(userId);
  }

  isHeadToHeadTieBreakerAvailable(userId: number): boolean {
    return Boolean(
      this.isTieBreakerAvailable(userId) &&
        (this.tie_breaker || _.get(this, 'next_tie_breaker', false)) &&
        !this._isMatchExpired()
    );
  }

  isBracketTieBreakerAvailable(userId: number): boolean {
    return Boolean(this.bracket && this.isTieBreakerAvailable(userId));
  }

  isPlayableTieBreaker(userId: number): boolean {
    const isTieBreakerMatch = this.tie_breaker;
    const isPending = !this.finished_at;
    const currentUser = this.getUserPlayer(userId);

    const isPendingTieBreaker = isTieBreakerMatch && isPending;
    const hasUserPlayed =
      currentUser != null && (currentUser.state == 'FINISHED' || currentUser.state == 'ABORTED');
    return isPendingTieBreaker && !hasUserPlayed;
  }

  isAborted(userId: number): boolean {
    const player = this.getUserPlayer(userId);
    if (!player) {
      return false;
    }
    return player.state == 'ABORTED';
  }

  prizeTierReached(userId: number): string {
    let finalPlace = '';
    const player = this.getUserPlayer(userId);
    if (!player) {
      return '';
    }
    if (Bracket.isChampionship(this.bracket_position)) {
      if (player.position == 1 && !this.isAborted(userId)) {
        finalPlace = _m('Winner');
      } else {
        finalPlace = _m('Finalist');
      }
    } else {
      const topX = Bracket.numberOfPlayersInBracketRound(this.bracket_position);
      finalPlace = _m('Top {0}', topX.toString());
    }
    return finalPlace;
  }

  checkPresentPlayerInTiebreaker(userId: any): boolean {
    if (this.players[0].user_id == userId) {
      return true;
    }
    return false;
  }

  getMatchCode(): string {
    return this.match_code;
  }

  getMedalsEarned(userId: string): number {
    if (this.isCompleted()) {
      const player = this.getUserPlayer(userId);
      if (player == null) {
        return 0;
      }
      return player.cash_prize || player.z_prize;
    }

    return 0;
  }

  shouldShowMatchXpMedalsEarned(userId: number): boolean {
    return (
      (this.isH2HMatch() || (this.bracket && !this.isBracketTemplateInProgress(userId))) &&
      this.isCompleted() &&
      !this.isHeadToHeadTieBreakerAvailable(userId)
    );
  }

  getTiebreakerPlayersInfo(userId: any): Object {
    const playerList = {};
    for (const player of this.players) {
      const user_id = player.user_id;
      const name = player.name;
      const score = player.score;
      const flag_url = player.flag_url;
      const avatar_url = player.avatar_url;
      let title = 'player_1';
      if (userId != player.user_id) {
        title = 'player_2';
      }

      playerList[title] = {
        user_id: user_id,
        name: name,
        score: score,
        flag_url: flag_url,
        avatar_url: avatar_url,
      };
    }
    return playerList;
  }

  getUserPlayer(userId: number): MatchPlayer {
    return (
      _.find(this.players, function (player: MatchPlayer): boolean {
        return player.user_id == userId;
      }) || null
    );
  }

  getPlayers(): Array<MatchPlayer> {
    return this.players;
  }

  hasZPrize(userId: number): boolean {
    return Boolean(this.getUsersZPrize(userId));
  }

  getUsersZPrize(userId: number): number {
    const player = this.getUserPlayer(userId);
    if (player == null) {
      return 0;
    }
    return player.z_prize || 0;
  }

  hasCashPrize(userId: number): boolean {
    return Boolean(this.getUsersCashPrize(userId));
  }

  getUsersCashPrize(userId: number): number {
    const player = this.getUserPlayer(userId);
    if (player == null) {
      return 0;
    }
    return player.cash_prize || 0;
  }

  hasBonusCashPrize(userId: number): boolean {
    return Boolean(this.getUsersBonusCashPrize(userId));
  }

  getUsersBonusCashPrize(userId: number): number {
    const player = this.getUserPlayer(userId);
    if (!player) {
      return 0;
    }
    return player.bonus_cash_prize || 0;
  }

  hasCustomPrize(userId: number): boolean {
    return Boolean(this.getUsersCustomPrize(userId));
  }

  getUsersCustomPrize(userId: number): any {
    const player = this.getUserPlayer(userId);
    if (player == null) {
      return 0;
    }
    return player.custom_prize;
  }

  hasTicketzPrize(userId: number): boolean {
    return Boolean(this.getUsersTicketzPrize(userId));
  }

  getUsersTicketzPrize(userId: number): number {
    const player = this.getUserPlayer(userId);
    if (player == null) {
      return 0;
    }
    return (player.ticketz_won_prize || 0) + (player.ticketz_prize || 0);
  }

  // Returns the ticketz the given user has won for the match. This excludes the participation ticketz
  getUsersTicketzWon(userId: number): number {
    const player = this.getUserPlayer(userId);
    if (player == null) {
      return 0;
    }
    return player.ticketz_won_prize || 0;
  }

  hasMedalPrize(userId: number): boolean {
    return Boolean(this.getUsersMedalsPrize(userId));
  }

  getUsersMedalsPrize(userId: number): number {
    const player = this.getUserPlayer(userId);
    if (player == null) {
      return 0;
    }
    return player.medals_prize || 0;
  }

  userHasPrize(userId: number): boolean {
    const player = this.getUserPlayer(userId);
    if (player == null) {
      return false;
    }
    return Boolean(
      player.cash_prize ||
        player.bonus_cash_prize ||
        player.ticketz_prize ||
        player.ticketz_won_prize ||
        player.z_prize ||
        player.custom_prize
    );
  }

  // Returns true if the given user has won a prize. This excludes the participation ticketz
  userHasWonPrize(userId: number): boolean {
    const player = this.getUserPlayer(userId);
    if (player == null) {
      return false;
    }
    return Boolean(
      player.cash_prize ||
        player.bonus_cash_prize ||
        player.ticketz_won_prize ||
        player.z_prize ||
        player.custom_prize
    );
  }

  usersPosition(userId: number): number {
    const player = this.getUserPlayer(userId);
    if (player) {
      return player.position;
    }
    return 0;
  }

  currentRound(userId: number): number {
    let roundNum = 0;
    if (this.didWinMatch(userId) && !this.isAborted(userId)) {
      const nextPos = Bracket.calculateNextPosition(this.bracket_position);
      roundNum = Bracket.calculateRound(nextPos);
    } else {
      roundNum = Bracket.calculateRound(this.bracket_position);
    }
    return roundNum;
  }

  isBracketTemplateInProgress(userId: number): boolean {
    if (Platform.OS == 'android') {
      // Could be cruft from merge
      let isInProgress = false;
      let didFindMatch = false;

      if (this.bracket) {
        const matchHistory = MatchesStore.getData();
        _.forEach(matchHistory, (match: MatchRecord) => {
          match = new Match(match);
          if (match?.tournament_id == this.tournament_id) {
            didFindMatch = true;
            if (
              (match.category == 'PENDING' || match.category == 'YOUR_TURN') &&
              !match.isCompleted()
            ) {
              isInProgress = true;
            }
          }
        });
      }
      if (!didFindMatch) {
        return this.category == 'PENDING';
      }
      return isInProgress;
    }

    if (this.bracket) {
      return (this.category == 'PENDING' || this.category == 'YOUR_TURN') && !this.isCompleted();
    }
    return false;
  }

  isLiveBracket(): boolean {
    // This is a strange check, but only live events have score messages, so this covers
    // all live brackets.
    return this.bracket != null && this.score_messages != null;
  }

  _isMatchExpired(): boolean {
    const endDate = new Date(this.expires_at);
    const currentDate = new Date();

    return endDate <= currentDate;
  }

  formattedEntryFee(userId?: number): string {
    if (this.entries_remaining != null) {
      return I18n.message('Prepurchased');
    }
    
    if (userId) {
      const userPlayer = this.getUserPlayer(userId);
      if (userPlayer && userPlayer.cash_entry_fee != null && userPlayer.cash_entry_fee > 0) {
        return I18n.getCurrencyString(userPlayer.cash_entry_fee, '$', false, false);
      }
    }
    
    if (this.z_fee) {
      return SkillzStyle.currencyValues.practiceCurrencyString + this.z_fee;
    } else if (this.cash_fee) {
      return I18n.getCurrencyString(this.cash_fee, '$', false, false);
    }
    return _m('Free');
  }

  hasReplay(): boolean {
    let hasReplay = false;
    if (this.finished_at || (this.type == 'UNLIMITED_ENTRY' && this.players.length > 1)) {
      _.forEach(this.players, (player: MatchPlayer) => {
        if (player.replay_url && player.replay_url.length) {
          hasReplay = true;
        }
      });
    }
    return hasReplay;
  }

  isFriendsMatch(): boolean {
    return this.type == 'LIMITED_ENTRY' && _.get(this.getMatchCode(), 'length') > 0;
  }

  isRematchChallenge(userId: number): boolean {
    const isRematchChallenge =
      !this.finished_at &&
      !this.bracket &&
      this.type != 'UNLIMITED_ENTRY' &&
      this.players != null &&
      this.players.length == 1 &&
      this.players[0].user_id != userId &&
      !this.tie_breaker;

    return isRematchChallenge;
  }

  getBracketBaseURL(isShare: boolean, userId: number): string {
    let baseURL = Bracket.getBracketBaseURL(this.bracket.id);
    baseURL = this.appendAnchorToBracketURL(baseURL, userId);
    baseURL = baseURL + '?match_position=' + this.bracket_position;

    if (!isShare) {
      baseURL = baseURL + '&spectate=false';
    }

    const practiceCur =
      '&practiceCur=' + encodeURI(SkillzStyle.currencyValues.practiceCurrencyString);

    baseURL = baseURL + practiceCur;

    return baseURL;
  }

  getTopOpponent(userId: number, returnPlaceholder: boolean): MatchPlayer {
    const players = this.players || [];
    const opponents = _.reject(players, function (n): boolean {
      return n.user_id == userId;
    });
    if (opponents.length) {
      return _.min(opponents, 'position');
    }
    return returnPlaceholder
      ? {
          name: I18n.message('Challenger'),
          thumbnail_url: 'skillz-generic-avatar',
          position: Number.MAX_SAFE_INTEGER,
        }
      : null;
  }

  getOpponentNames(userId: string): Array<String> {
    const players = this.players || [];
    const opponents = _.reject(players, function (n: Object): boolean {
      return n.user_id == userId;
    });

    const names = [];
    opponents.map((player: Object) => {
      if (player.name !== userId) {
        names.push(player.name);
      }
    });
    return names;
  }

  getTopOpponentAvatarURL(userId: string, returnPlaceholder: boolean): string {
    const opponent = this.getTopOpponent(userId, returnPlaceholder);
    if (opponent) {
      const player = new MatchPlayer(opponent);
      return player.getThumbNailURL();
    }
    return 'skillz-generic-avatar';
  }

  isAtLeastOnePlayerMissing(): boolean {
    let playersNull = 0;
    for (let i = 0; i < this.num_players; ++i) {
      playersNull += _.get(this.players[i], 'user_id') ? 0 : 1;
    }
    return playersNull > 0;
  }

  winnerOfMatch(): MatchPlayer {
    const winner: MatchPlayer = _.find(this.players, {outcome: 'WIN'});
    return winner;
  }

  appendAnchorToBracketURL(baseURL: string, userId: number): string {
    let matchPosition = this.bracket_position;

    if (this.didWinMatch(userId) && !(this.isTie(userId) && !this.isAborted(userId))) {
      const nextPos = Bracket.calculateNextPosition(this.bracket_position);
      if (nextPos) {
        matchPosition = nextPos;
      }
    }

    let anchor = '';
    if (Bracket.isChampionship(matchPosition)) {
      if (this.didWinMatch(userId) && _.get(this.bracket, 'state', '') == 'CONCLUDED') {
        anchor = 'CHAMPION';
      } else {
        anchor = 'FINALS';
      }
    } else {
      anchor = matchPosition;
    }
    return baseURL + '#' + anchor;
  }

  getHighestScore(): number {
    const players = this.players;
    let bestScore = 0;

    if (Array.isArray(players)) {
      players.forEach((player: MatchPlayer): number => {
        if (player.score > bestScore) {
          bestScore = player.score;
        }
      });
    }
    return bestScore;
  }

  getUserBestScore(userId: number): number {
    const players = this.players;
    let bestScore = 0;

    if (Array.isArray(players)) {
      players.forEach((player: MatchPlayer): number => {
        if (player.user_id == userId) {
          if (player.score == null) {
            return bestScore;
          }
          bestScore = player.score;
        }
      });
    }
    return bestScore;
  }

  getUserScore(userId: number): number {
    const player = this.getUserPlayer(userId);
    if (!player || player.score == null) {
      return 0;
    }
    return player.score;
  }

  matchTypeEnumFromString(typeString: string): string {
    const SKZReactMatchCompletionManager = NativeModules.SKZReactMatchCompletionManager;
    const TournamentTypes = SKZReactMatchCompletionManager.TournamentType;
    switch (typeString) {
      case 'REMATCH':
        return TournamentTypes.REMATCH;
      case 'TURN_BASED':
        return TournamentTypes.TURN_BASED;
      case 'UNLIMITED_ENTRY':
        return TournamentTypes.UNLIMITED_ENTRY;
      case 'LIMITED_ENTRY':
      default:
        return TournamentTypes.LIMITED_ENTRY;
    }
  }

  matchStateEnumFromCategoryString(categoryString: string) {
    if (Platform.OS == 'android') {
      let matchState;
      if (this.bracket) {
        const matchHistory = MatchesStore.getData();
        _.forEach(matchHistory, (match: MatchRecord) => {
          match = new Match(match);
          if (match?.tournament_id == this.tournament_id) {
            matchState = match.category;
          }
        });
      }
      switch (categoryString) {
        case 'COMPLETED':
          return matchState;
        case 'CANCELED':
          return matchState;
        case 'PENDING':
        case 'YOUR_TURN':
        default:
          return matchState;
      }
    } else {
      const SKZReactMatchCompletionManager = NativeModules.SKZReactMatchCompletionManager;
      const TournamentStates = SKZReactMatchCompletionManager.TournamentState;
      switch (categoryString) {
        case 'COMPLETED':
          return TournamentStates.Closed;
        case 'CANCELED':
          return TournamentStates.Canceled;
        case 'PENDING':
        case 'YOUR_TURN':
        default:
          return TournamentStates.Started;
      }
    }
  }

  playerStatusEnumFromString(statusString) {
    const SKZReactMatchCompletionManager = NativeModules.SKZReactMatchCompletionManager;
    const PlayerStatus = SKZReactMatchCompletionManager.PlayerStatus;
    switch (statusString) {
      case 'ABORTED':
        return PlayerStatus.ABORTED;
      case 'FINISHED':
        return PlayerStatus.FINISHED;
      case 'STARTED':
      default:
        return PlayerStatus.STARTED;
    }
  }

  insertMatchIntoCoreData(userId, shouldShowIndicator, completion) {
    if (Platform.OS === 'android' || Platform.OS === 'web') {
      completion();
      return;
    }

    if (shouldShowIndicator) {
      LoadingIndicator.show();
    }

    const newMatch = {
      _isa: 'SKZTournamentSummary',
      id: parseInt(this.id),
      rematch: this.is_rematch,
      dateEnded: this.finished_at,
      type: this.matchTypeEnumFromString(this.type),
      templateId: this.tournament_id,
      name: this.name,
      state: this.matchStateEnumFromCategoryString(this.category),
      playerCount: _.size(this.players),
      entryFee: this.cash_fee,
      entryPoints: this.z_fee,
      lastUpdated: this.finished_at,
      expire: this.expires_at,
      playersTurn: this.category == 'YOUR_TURN',
    };

    if (this.score_messages) {
      newMatch.your = this.score_messages.your;
      newMatch.best = this.score_messages.best;
      newMatch.lastEntry = this.score_messages.lastEntry;
      newMatch.inProgress = this.score_messages.in_progress;
    }

    newMatch.playerObjects = [];
    let usersPlayer = null;

    const newPlayerList = this.players || [];
    newPlayerList.forEach((newPlayer) => {
      if (newPlayer.user_id == userId) {
        usersPlayer = newPlayer;
        newMatch.position = newPlayer.position;
        if (newPlayer.best_score) {
          newMatch.score = newPlayer.best_score.toString();
        } else if (newPlayer.score) {
          newMatch.score = newPlayer.score.toString();
        }
      }

      const plowPlayer = {
        _isa: 'SKZPlayerMetric',
        tournamentPlayerId: parseInt(newPlayer.id),
        status: this.playerStatusEnumFromString(newPlayer.state),
        tauntText: newPlayer.shoutout,
        position: newPlayer.position,
        profilePictureUrl: newPlayer.avatar_url,
        flagUrl: newPlayer.flag_url,
        prize: newPlayer.cash_prize,
        prizePoints: newPlayer.z_prize,
        prizeTicketz: newPlayer.ticketz_prize,
        thumbnailUrl: newPlayer.thumbnail_url,
        replayUrl: newPlayer.replay_url,
        username: newPlayer.name,
        playingNow: newPlayer.state != 'FINISHED',
        started: newPlayer.finished_at,
        dateReported: newPlayer.finished_at,
        hasReplay: newPlayer.replay_url != null,
      };

      if (newPlayer.user_id) {
        plowPlayer.secUserId = newPlayer.user_id.toString();
      }

      if (newPlayer.best_score) {
        plowPlayer.score = newPlayer.best_score.toString();
      } else if (newPlayer.score) {
        plowPlayer.score = newPlayer.score.toString();
      }

      newMatch.playerObjects.push(plowPlayer);
    });

    TournamentStore.getTournamentById(this.tournament_id).then((tournament) => {
      newMatch.description = tournament.description;
      newMatch.entriesRequired = tournament.entries_required;
      newMatch.maxNumOfPlayers = tournament.max_players;
      newMatch.numOfPrizes = tournament.prizes
        ? tournament.prizes.length
        : tournament.custom_prizes.length;
      newMatch.prizePoints = tournament.total_z_prize;
      newMatch.prize = tournament.total_cash_prize;

      if (tournament.live_event) {
        newMatch.event = {
          _isa: 'SKZEvent',
          name: tournament.live_event.name,
          description: tournament.live_event.description,
          id: tournament.live_event.id,
          entries: tournament.live_event.entries,
          eventUrl: tournament.live_event.event_url,
          userRegistered: tournament.live_event.user_registered,
          shareMessage: tournament.live_event.share_message,
          signupUrl: tournament.live_event.signup_url,
          subscribed: tournament.live_event.subscribed,
          streamUrl: tournament.live_event.stream_url,
          logo: tournament.live_event.logo,
          endDate: tournament.live_event.end,
          startDate: tournament.live_event.start,
        };

        if (tournament.entries_required) {
          newMatch.entryOffers = [];
          if (tournament.ticket_offers && tournament.ticket_offers.length) {
            tournament.ticket_offers.forEach((offer) => {
              //This might be able to be shortened since we're just adding _isa?
              newMatch.entryOffers.push({
                _isa: 'SKZEventEntryOffer',
                id: offer.id,
                title: offer.title,
                amount: offer.amount,
                entries: offer.entries,
                start: offer.start,
                end: offer.end,
              });
            });
          }
        }
      }

      if (tournament.custom_prizes && tournament.custom_prizes.length) {
        newMatch.customPrizes = [];

        const firstPrize = tournament.custom_prizes[0];
        newMatch.customPrizes.push({
          _isa: 'SKZCustomPrize',
          position: firstPrize.position,
          name: firstPrize.name,
          description: firstPrize.description,
          id: firstPrize.position + '-' + firstPrize.description + '-' + newMatch.id,
        });
      } else if (tournament.prizes && tournament.prizes.length) {
        newMatch.prizes = [];

        const firstPrize = tournament.prizes[0];
        newMatch.prizes.push({
          _isa: 'SKZPrize',
          position: firstPrize.position,
          prize: firstPrize.cash_prize,
          prizePoints: firstPrize.z_prize,
          id:
            firstPrize.z_prize +
            '-' +
            firstPrize.cash_prize +
            '-' +
            firstPrize.position +
            '-' +
            newMatch.id,
        });
      }

      SKZCoreDataWrapper.createCoreDataObject(newMatch, () => {
        if (completion) {
          completion();
        }

        if (shouldShowIndicator) {
          LoadingIndicator.hide();
        }
      });
    });
  }

  createAWSMatchObjectFromMatch(userId: number): AWSParams {
    return {
      result: JSON.stringify({
        tournament_id: this.tournament_id,
        started_at: this.started_at,
        user_id: userId,
        rules: this.rules,
        random_number_seed: this.random_number_seed,
        random_number_seeds: this.random_number_seeds,
        random_number_strategy: this.random_number_strategy,
        selected_random_seed: this._getSeedStrategy(),
      }),
    };
  }

  _getSeedStrategy(): string {
    return this.random_number_strategy === MERSENNE ? RANDOM_NUMBER_SEEDS : RANDOM_NUMBER_SEED;
  }

  /**
   * Helper function used to return the player object for a trainer bot
   */
  private static getTrainerOpponent(data?: Object): Object | undefined {
    return data?.players.find(({is_bot}) => is_bot);
  }

  /**
   * Helper function used to modify the match data for display purposes
   */
  private static getMatchData(data?: Object): Object | undefined {
    if (!data || !ClientPropertyConstants.ftueConversionsEnabled) return data;

    if (Match.getTrainerOpponent(data)) {
      // If this was a match against a trainer, we'll override the template name
      // with "Training Match"
      return {
        ...data,
        name: I18n.message('Training Match'),
      };
    }

    return data;
  }

  static Status = {
    ABORTED: 'ABORTED',
    FINISHED: 'FINISHED',
    STARTED: 'STARTED',
  };
}
